import Phaser from 'phaser'

export const INACTIVITY_MODAL_TEXTURE_KEY = 'inactivity-modal'
export const INACTIVITY_MODAL_TEXTURE_PATH = 'assets/modal.png'

const DEFAULT_TITLE = 'Alerta inactividad'
const DEFAULT_MESSAGE = 'Se ha detectado que\nno hay actividad,\nel juego se reiniciará en'
const DEFAULT_FALLBACK_WIDTH = 501
const DEFAULT_FALLBACK_HEIGHT = 271
const DEFAULT_TITLE_Y_RATIO = 0.08
const DEFAULT_MESSAGE_Y_RATIO = 0.45
const DEFAULT_COUNTDOWN_Y_RATIO = 0.70
const DEFAULT_MESSAGE_LINE_SPACING = 8
const DEFAULT_MESSAGE_COLOR = '#1e49a0'
const DEFAULT_COUNTDOWN_COLOR = '#1e49a0'
const DEFAULT_TITLE_COLOR = '#ffffff'
const DEFAULT_FONT_FAMILY = '"Press Start 2P", system-ui'
const DEFAULT_FONT_LOAD_RULE = '14px "Press Start 2P"'

export type InactivityModalOptions = {
	depth: number
	title?: string
	message?: string
	titleYRatio?: number
	messageYRatio?: number
	titleColor?: string
	messageColor?: string
	countdownColor?: string
	titleFontSize?: string
	messageFontSize?: string
	countdownFontSize?: string
	fontFamily?: string
	fontLoadRule?: string
	fallbackWidth?: number
	fallbackHeight?: number
}

export type InactivityModal = {
	container: Phaser.GameObjects.Container
	titleText: Phaser.GameObjects.Text
	messageText: Phaser.GameObjects.Text
	countdownText: Phaser.GameObjects.Text
	show: () => void
	hide: () => void
	setCountdownSeconds: (seconds: number | null) => void
	reposition: () => void
	destroy: () => void
}

export const createInactivityModal = (
	scene: Phaser.Scene,
	options: InactivityModalOptions
): InactivityModal => {
	const fontFamily = options.fontFamily ?? DEFAULT_FONT_FAMILY
	const title = options.title ?? DEFAULT_TITLE
	const message = options.message ?? DEFAULT_MESSAGE
	const titleYRatio = options.titleYRatio ?? DEFAULT_TITLE_Y_RATIO
	const messageYRatio = options.messageYRatio ?? DEFAULT_MESSAGE_Y_RATIO
	const countdownYRatio = DEFAULT_COUNTDOWN_Y_RATIO
	const fallbackWidth = options.fallbackWidth ?? DEFAULT_FALLBACK_WIDTH
	const fallbackHeight = options.fallbackHeight ?? DEFAULT_FALLBACK_HEIGHT

	const modal = scene.add.container(scene.scale.width * 0.5, scene.scale.height * 0.5)
	modal.setDepth(options.depth)
	modal.setScrollFactor(0)
	modal.setVisible(false)

	let modalWidth = fallbackWidth
	let modalHeight = fallbackHeight

	if (scene.textures.exists(INACTIVITY_MODAL_TEXTURE_KEY))
	{
		const background = scene.add.image(0, 0, INACTIVITY_MODAL_TEXTURE_KEY)
		const source = scene.textures.get(INACTIVITY_MODAL_TEXTURE_KEY).getSourceImage() as { width: number, height: number }
		modalWidth = source.width
		modalHeight = source.height
		background.setDisplaySize(modalWidth, modalHeight)
		modal.add(background)
	}
	else
	{
		const fallback = scene.add.rectangle(0, 0, modalWidth, modalHeight, 0x173a84, 0.95)
		modal.add(fallback)
	}

	const titleText = scene.add.text(
		0,
		(-modalHeight * 0.5) + (modalHeight * titleYRatio),
		title,
		{
			fontFamily,
			fontSize: options.titleFontSize ?? '14px',
			color: options.titleColor ?? DEFAULT_TITLE_COLOR,
			align: 'center'
		}
	).setOrigin(0.5)

	const messageText = scene.add.text(
		0,
		(-modalHeight * 0.5) + (modalHeight * messageYRatio),
		message,
		{
			fontFamily,
			fontSize: options.messageFontSize ?? '12px',
			color: options.messageColor ?? DEFAULT_MESSAGE_COLOR,
			align: 'center',
			wordWrap: { width: modalWidth * 0.82, useAdvancedWrap: true }
		}
	).setOrigin(0.5)
	messageText.setLineSpacing(DEFAULT_MESSAGE_LINE_SPACING)

	const countdownText = scene.add.text(
		0,
		(-modalHeight * 0.5) + (modalHeight * countdownYRatio),
		'',
		{
			fontFamily,
			fontSize: options.countdownFontSize ?? '26px',
			color: options.countdownColor ?? DEFAULT_COUNTDOWN_COLOR,
			align: 'center'
		}
	).setOrigin(0.5)
	countdownText.setVisible(false)

	modal.add([titleText, messageText, countdownText])

	const onResize = () => {
		modal.setPosition(scene.scale.width * 0.5, scene.scale.height * 0.5)
	}

	scene.scale.on(Phaser.Scale.Events.RESIZE, onResize)

	const fontLoadRule = options.fontLoadRule ?? DEFAULT_FONT_LOAD_RULE
	const fontSet = document.fonts
	if (fontSet)
	{
		fontSet.load(fontLoadRule)
			.then(() => fontSet.ready)
			.then(() => {
				if (titleText.active)
				{
					titleText.setFontFamily(fontFamily)
				}
				if (messageText.active)
				{
					messageText.setFontFamily(fontFamily)
				}
				if (countdownText.active)
				{
					countdownText.setFontFamily(fontFamily)
				}
			})
			.catch(() => {})
	}

	const setCountdownSeconds = (seconds: number | null) => {
		if (!countdownText.active)
		{
			return
		}

		if (seconds === null)
		{
			countdownText.setText('')
			countdownText.setVisible(false)
			return
		}

		const safeSeconds = Math.max(0, Math.ceil(seconds))
		countdownText.setText(`${safeSeconds}s`)
		countdownText.setVisible(true)
	}

	return {
		container: modal,
		titleText,
		messageText,
		countdownText,
		show: () => {
			modal.setVisible(true)
		},
		hide: () => {
			modal.setVisible(false)
		},
		setCountdownSeconds,
		reposition: onResize,
		destroy: () => {
			scene.scale.off(Phaser.Scale.Events.RESIZE, onResize)
			modal.destroy()
		}
	}
}